import React, { memo, useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import debounce from 'lodash/debounce';
import { Button } from '@100mslive/roomkit-react';
import faceDetected from '../../../../assets/images/account_circle_green.svg';
import faceNotDetected from '../../../../assets/images/account_circle_red.svg';
import faceDetection from '../../../../assets/images/faceDetection.svg';
import logo from '../../../../assets/images/incruiter_logo.svg';
import practiceActiveVector from '../../../../assets/images/practice-active-vector.svg';
import practiceDisabledVector from '../../../../assets/images/practice-disabled-vector.svg';
import {
    fetchScreenShotImage,
    fullFaceDetectionNew,
    practiceQuestion,
    practiceQuestionUpdate,
    updateCandidate,
} from '../../../../redux/action';
import PreviewTile from './PreviewTile';
import Modal from '../../../commonComponent/Modal/Modal';
import { isMobileOrTabletDevice, isValidDataURI } from '../../../../utils/helper';
import { compressImage } from '../../utils/utility';
import { FACE_DETECTED } from '../../utils/constants';
const FaceNotDetectedContent = () => {
    return (
        <div className="face-detection-modal-content d-center gap-10 mb-3">
            <img src={faceDetection} className="face-detection-logo" alt="Face Detection" />
            <div className="d-center flex-column">
                <div>
                    Uh-oh! It looks like your face is not fully visible or not detected by the camera. To help with
                    proper face detection, please try the following:
                </div>
                <div className="d-flex flex-column">
                    <div className="text-start">
                        <span className="f-bol">1. Sit upright</span> and ensure your face is centered in the camera
                        frame.
                    </div>
                    <div className="text-start">
                        <span className="f-bol">2. Adjust the lighting</span> so your face is well-lit and clearly
                        visible.
                    </div>
                    <div className="text-start">
                        <span className="f-bol">3. Move closer to the camera</span> if you're sitting too far away.
                    </div>
                    <div className="text-start">
                        <span className="f-bol">4. Check your camera angle</span> and ensure it is positioned at eye
                        level.
                    </div>
                </div>
            </div>
        </div>
    );
};

const AcknowledgeModal = memo(
    ({ form, onJoin, name, error, value, token, speakingPermission, videoContainerRef, isButtonDisabled }) => {
        const navigate = useNavigate();
        const dispatch = useDispatch();
        const { roomId, role } = useParams();
        const [isStarting, setIsStarting] = useState(false);
        const [practiceStatus, setPracticeStatus] = useState(false);
        const [hasFullFaceDetected, setHasFullFaceDetected] = useState(FACE_DETECTED.DEFAULT);
        const [faceDetectionError, setFaceDetectionError] = useState('');
        const [isProcessing, setIsProcessing] = useState(false);
        const [showModal, setShowModal] = useState(false);
        const canvasRef = useRef(null);
        const timerRef = useRef(null);
        const acknowledgeText = `Hello, my name is ${name}, and I acknowledge my identity verification for user authentication.`;
        const sendCompressedFrame = async (base64Image) => {
            if (isProcessing || hasFullFaceDetected === FACE_DETECTED.DETECTED) return false;
            setIsProcessing(true);
            try {
                const result = await new Promise((resolve, reject) => {
                    dispatch(
                        fullFaceDetectionNew({
                            data: { image_base64: base64Image },
                            onSuccess: (detected, errorMessage) => {
                                setHasFullFaceDetected(detected ? FACE_DETECTED.DETECTED : FACE_DETECTED.NOT_DETECTED);
                                if (base64Image && detected) {
                                    dispatch(
                                        fetchScreenShotImage({
                                            image: base64Image,
                                            interviewId: roomId,
                                            type: 10,
                                            callback: (image) => {
                                                if (isValidDataURI(image)) {
                                                    dispatch(updateCandidate({ roomId, candidateImage: image }));
                                                }
                                            },
                                        })
                                    );
                                }
                                setFaceDetectionError(errorMessage || '');
                                resolve(detected);
                            },
                            onFailure: (error) => {
                                console.error('Face detection error: ', error?.message);
                                reject(error);
                            },
                        })
                    );
                });

                return result;
            } catch (error) {
                console.error('Error in face detection:', error);
                return false;
            } finally {
                setIsProcessing(false);
                setIsStarting(false);
            }
        };

        const startModalTimer = () => {
            clearTimeout(timerRef.current);
            timerRef.current = setTimeout(() => {
                if (hasFullFaceDetected !== FACE_DETECTED.DETECTED) setShowModal(true);
            }, 20000);
        };

        const closeModal = () => {
            setShowModal(false);
            startModalTimer();
        };

        useEffect(() => {
            const processFaceDetection = async () => {
                if (hasFullFaceDetected === FACE_DETECTED.DETECTED) return;
                try {
                    const compressedImage = await compressImage({
                        canvasRef,
                        onFailure: () => setHasFullFaceDetected(false),
                    });
                    const faceDetected = await sendCompressedFrame(compressedImage);

                    // If face is not detected and component is still mounted, schedule next detection
                    if (faceDetected) {
                        clearTimeout(timerRef.current);
                    } else if (hasFullFaceDetected !== FACE_DETECTED.DETECTED) {
                        requestAnimationFrame(processFaceDetection);
                    }
                } catch (error) {
                    console.error('Error in face detection process:', error);
                    // Optionally, add a delay before retrying
                    if (hasFullFaceDetected !== FACE_DETECTED.DETECTED) {
                        setTimeout(() => requestAnimationFrame(processFaceDetection), 1000);
                    }
                }
            };

            // Start the face detection process immediately
            processFaceDetection();
            startModalTimer();

            return () => {
                clearTimeout(timerRef.current);
            };
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, []);

        useEffect(() => {
            dispatch(
                practiceQuestion({
                    roomId: roomId,
                    callback: (status) => {
                        if (status) setPracticeStatus(true);
                    },
                })
            );
        }, [dispatch, roomId]);

        const handlePracticeTest = () => {
            dispatch(practiceQuestionUpdate({ roomId }));
            const meetingURL = `/room/practice/${role}/${roomId}`;
            navigate(meetingURL, { state: { status: true, form: form, token: token } });
        };

        const handleSubmit = async () => {
            setIsStarting(true);
            const hasTranscribed = await new Promise((resolve) => {
                debounce(() => resolve(value || !speakingPermission), 300)();
            });
            if (hasTranscribed) onJoin();
            else setIsStarting(false);
        };

        const practiceStatusFinal = !(isStarting || isButtonDisabled) && practiceStatus;

        const renderPractice = () => {
            return practiceStatusFinal ? (
                <div className="practice-test">
                    <span onClick={handlePracticeTest}>
                        Practice Before Start
                        <img src={practiceActiveVector} className="practice-vector" alt="Practice Test" />
                    </span>
                </div>
            ) : (
                <div className="practice-test-disabled">
                    Practice Before Start
                    <img src={practiceDisabledVector} className="practice-vector" alt="Practice Test" />
                </div>
            );
        };

        const renderFaceDetect = (isMobile = false) => {
            const isDetect = hasFullFaceDetected === FACE_DETECTED.DETECTED;
            return (
                <div
                    className="w-100 align-items-center mt-1"
                    style={{
                        color: 'white',
                        fontSize: isMobile ? '12px' : '14px',
                        padding: isMobile ? '0px 24px' : '0px',
                    }}
                >
                    {hasFullFaceDetected !== FACE_DETECTED.DEFAULT && (
                        <div className={`me-3 ${isDetect ? 'green-color' : 'red-color'}`}>
                            <img className="me-1" src={isDetect ? faceDetected : faceNotDetected} alt="Face Detect" />
                            {hasFullFaceDetected}
                        </div>
                    )}
                    {hasFullFaceDetected === FACE_DETECTED.NOT_DETECTED && faceDetectionError && (
                        <div>{faceDetectionError}</div>
                    )}
                </div>
            );
        };

        const renderMobileView = () => {
            return (
                <>
                    <img src={logo} alt="logo" className="header-image" />
                    <div className="audio-video-check-sub-container-1">
                        <div className="getting-started pb-2">Get Started</div>
                        <div className="getting-started-sub pb-1">Setup your audio and video before joining</div>
                    </div>
                    <div className="audio-video-check-sub-container-2">
                        <PreviewTile name={name} error={error} isPreview auto />
                        {renderFaceDetect(true)}
                        <div className="sample-read-text">
                            Please read the line below to test your camera and microphone before starting the interview
                        </div>
                    </div>
                    <div className="audio-video-check-sub-container-3">
                        <div className="acknowledge-text-m">{acknowledgeText}</div>
                        <div className={`transcript-container ${value ? '' : 'transcript-text-disabled'}} `}>
                            {value ?? 'Transcript'}
                        </div>
                        <div className="acknowledge_title_line line-margin"></div>
                        <Button
                            type="submit"
                            disabled={isStarting || isButtonDisabled}
                            onClick={handleSubmit}
                            className={`mx-auto d-flex start-transcript-check-btn${
                                isStarting || isButtonDisabled ? '-disabled' : ''
                            }`}
                        >
                            Start Interview
                        </Button>
                        {renderPractice()}
                    </div>
                </>
            );
        };

        return (
            <div
                className={!isMobileOrTabletDevice() ? 'interview-preview-page' : 'audio-video-check-main-container'}
                ref={videoContainerRef}
            >
                <canvas ref={canvasRef} className="d-none" />
                <Modal
                    isOpen={showModal}
                    showCloseButton
                    onClose={closeModal}
                    headerText="Face Not Detected!"
                    Content={FaceNotDetectedContent}
                    buttonText="Okay"
                    handleClick={closeModal}
                    bodyStyle="p-0"
                    headerStyle="f-sem-bol f-24"
                />
                {isMobileOrTabletDevice() ? (
                    renderMobileView()
                ) : (
                    <div className="d-flex preview-page-layout">
                        <div className="w-50 preview-tile-div">
                            <div className="audio-video-check-sub-container-1">
                                <div className="finish-title">Get Started</div>
                                <div className="finish-title-main pb-3 pt-2">
                                    Setup your audio and video before joining
                                </div>
                            </div>
                            <PreviewTile name={name} error={error} hideLabel auto />
                            {renderFaceDetect(false)}
                        </div>
                        <div
                            className="d-flex flex-column justify-content-end pb-0 w-50"
                            style={{ padding: '0px 15px', margin: 'auto' }}
                        >
                            <div className="finish-title-m">
                                Please read the line below to test your camera and microphone before starting the
                                interview
                            </div>
                            <div className="dark-bg">
                                <p className="acknowledge_text-m my-auto">{acknowledgeText}</p>
                                <div className="px-2 py-2">
                                    <div className={`acknowledge_text_transcript_${value ? 'new' : 'disable_new'}`}>
                                        {value ? value : 'Please wait while speech is being transcribed'}
                                    </div>
                                </div>
                                <div className="acknowledge_title_line mb-2" />
                                <div className="d-flex justify-content-center">
                                    <Button
                                        type="submit"
                                        disabled={
                                            isStarting ||
                                            isButtonDisabled ||
                                            hasFullFaceDetected !== FACE_DETECTED.DETECTED
                                        }
                                        onClick={handleSubmit}
                                        className={`mx-auto d-flex start-transcript-check-btn${
                                            isStarting ||
                                            isButtonDisabled ||
                                            hasFullFaceDetected !== FACE_DETECTED.DETECTED
                                                ? '-disabled'
                                                : ''
                                        }`}
                                    >
                                        Start Interview
                                    </Button>
                                </div>
                            </div>
                            {renderPractice()}
                        </div>
                    </div>
                )}
            </div>
        );
    }
);

export default AcknowledgeModal;
